import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Trash2, ArrowLeft, Loader2, ShieldCheck, ShieldAlert, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  listComparisons,
  getComparison,
  deleteComparison,
  listQA,
  getQA,
  deleteQA,
  listValidations,
  getValidation,
  deleteValidation,
} from "@/lib/api";
import ComparisonResult from "@/components/ComparisonResult";
import QAResult from "@/components/QAResult";
import ValidationResult from "@/components/ValidationResult";

export default function HistoryPage() {
  const [comps, setComps] = useState([]);
  const [qas, setQas] = useState([]);
  const [vals, setVals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeComp, setActiveComp] = useState(null);
  const [activeQa, setActiveQa] = useState(null);
  const [activeVal, setActiveVal] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, q, v] = await Promise.all([listComparisons(), listQA(), listValidations()]);
      setComps(c);
      setQas(q);
      setVals(v);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const active = activeComp || activeQa || activeVal;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">History</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            {comps.length} diff{comps.length === 1 ? "" : "s"} · {qas.length} QA · {vals.length} validation{vals.length === 1 ? "" : "s"}
          </p>
        </div>
        {active && (
          <Button
            variant="outline"
            data-testid="history-back-btn"
            onClick={() => {
              setActiveComp(null);
              setActiveQa(null);
              setActiveVal(null);
            }}
            className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to list
          </Button>
        )}
      </header>

      <div className="p-8">
        {activeComp ? (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold">{activeComp.name}</h2>
            <ComparisonResult comparison={activeComp} />
          </div>
        ) : activeQa ? (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold">{activeQa.name}</h2>
            <QAResult qa={activeQa} />
          </div>
        ) : activeVal ? (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold">{activeVal.file_name}</h2>
            <ValidationResult validation={activeVal} />
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <Tabs defaultValue="qa">
            <TabsList className="bg-[#101010] border border-zinc-800 rounded-sm p-1 mb-4">
              <TabsTrigger value="qa" data-testid="history-tab-qa" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                QA Reports ({qas.length})
              </TabsTrigger>
              <TabsTrigger value="validations" data-testid="history-tab-validations" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                Validations ({vals.length})
              </TabsTrigger>
              <TabsTrigger value="diffs" data-testid="history-tab-diffs" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                Diffs ({comps.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="qa">
              {qas.length === 0 ? (
                <Empty icon={ShieldCheck} text="No QA reports yet. Run one from the QA Tolerance tab." />
              ) : (
                <div className="space-y-2">
                  {qas.map((q) => {
                    const conforming = q.summary.verdict === "CONFORMING";
                    return (
                      <div
                        key={q.id}
                        data-testid={`qa-history-row-${q.id}`}
                        onClick={() => getQA(q.id).then(setActiveQa).catch(() => toast.error("Could not load"))}
                        className="border border-zinc-800 rounded-sm bg-[#101010] hover:bg-zinc-900/60 transition-colors duration-75 px-5 py-4 flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          {conforming ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <ShieldAlert className="w-5 h-5 text-red-400" />}
                          <div>
                            <div className="font-mono text-sm text-zinc-200">{q.name}</div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-1">
                              {q.standard_name} · {new Date(q.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-display text-lg font-bold" style={{ color: conforming ? "#34D399" : "#FBBF24" }}>
                            {q.summary.conformance_pct}%
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`delete-qa-${q.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteQA(q.id).then(() => {
                                setQas((x) => x.filter((i) => i.id !== q.id));
                                toast.success("QA report deleted");
                              });
                            }}
                            className="h-8 w-8 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="validations">
              {vals.length === 0 ? (
                <Empty icon={BadgeCheck} text="No validations yet. Run one from the Validate tab." />
              ) : (
                <div className="space-y-2">
                  {vals.map((v) => {
                    const compliant = v.summary.verdict === "COMPLIANT";
                    return (
                      <div
                        key={v.id}
                        data-testid={`val-history-row-${v.id}`}
                        onClick={() => getValidation(v.id).then(setActiveVal).catch(() => toast.error("Could not load"))}
                        className="border border-zinc-800 rounded-sm bg-[#101010] hover:bg-zinc-900/60 transition-colors duration-75 px-5 py-4 flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          {compliant ? <BadgeCheck className="w-5 h-5 text-emerald-400" /> : <ShieldAlert className="w-5 h-5 text-red-400" />}
                          <div>
                            <div className="font-mono text-sm text-zinc-200">{v.file_name}</div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-1">
                              {v.summary.total_errors} errors · {v.summary.total_warnings} warnings · {new Date(v.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-display text-lg font-bold" style={{ color: compliant ? "#34D399" : "#FBBF24" }}>
                            {v.summary.compliance_pct}%
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`delete-val-${v.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteValidation(v.id).then(() => {
                                setVals((x) => x.filter((i) => i.id !== v.id));
                                toast.success("Validation deleted");
                              });
                            }}
                            className="h-8 w-8 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="diffs">
              {comps.length === 0 ? (
                <Empty icon={History} text="No comparisons yet. Run a diff from the Compare tab." />
              ) : (
                <div className="space-y-2">
                  {comps.map((c) => (
                    <div
                      key={c.id}
                      data-testid={`history-row-${c.id}`}
                      onClick={() => getComparison(c.id).then(setActiveComp).catch(() => toast.error("Could not load"))}
                      className="border border-zinc-800 rounded-sm bg-[#101010] hover:bg-zinc-900/60 transition-colors duration-75 px-5 py-4 flex items-center justify-between cursor-pointer"
                    >
                      <div>
                        <div className="font-mono text-sm text-zinc-200">{c.name}</div>
                        <div className="font-mono text-[11px] text-zinc-500 mt-1">
                          {new Date(c.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3 font-mono text-xs">
                          <span className="text-emerald-400">+{c.summary.added}</span>
                          <span className="text-red-400">-{c.summary.removed}</span>
                          <span className="text-amber-400">~{c.summary.modified}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`delete-comparison-${c.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteComparison(c.id).then(() => {
                              setComps((x) => x.filter((i) => i.id !== c.id));
                              toast.success("Comparison deleted");
                            });
                          }}
                          className="h-8 w-8 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="grid-texture border border-zinc-800 rounded-sm py-24 flex flex-col items-center text-center">
      <Icon className="w-10 h-10 text-zinc-700 mb-4" />
      <p className="text-sm text-zinc-500">{text}</p>
    </div>
  );
}
